// src/pages/TemplateGallery.jsx
//
// The Template Gallery: every structure an author can start a draft from, as a
// grid of cards with a format mockup, an expandable outline, and a button that
// opens the app's existing Create dialog with that template selected.
//
// Reached from the Library sidebar, under MY LIBRARY. It is its own route
// rather than another `viewMode` inside Library.jsx because a gallery is
// something you link someone to — `viewMode` is local state with no URL — and
// because Library.jsx is long enough already.
//
// **Persistence.** Nothing here writes. The catalogue is read from
// `GET /api/manuscript-templates`, seeded by
// `backend/tools/seed_manuscript_templates.py`; choosing a template hands its id
// to NewDocumentModal, and `POST /api/drafts` does the seeding server-side.
//
// Colours come from `templateTokens.js`, which re-exports the mapping in
// `structureTokens.js` — the prototype's own palette is not used. The page keeps
// the app's global <Navbar /> by staying out of `OWN_NAV_ROUTES` in App.js.
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

import { API_BASE } from '../utils/api';
import CategoryFilter from '../components/templates/CategoryFilter';
import TemplateCard from '../components/templates/TemplateCard';
import { SP, FONT, BP, eyebrowSx } from '../components/templates/templateTokens';
import NewDocumentModal from '../components/editor/NewDocumentModal';

export default function TemplateGallery() {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  // Several cards can be open at once, which is the point of expanding in place
  // — comparing two outlines is the reason an author opens either of them.
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const [createOpen, setCreateOpen] = useState(false);
  const [chosenTemplateId, setChosenTemplateId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const accessToken = localStorage.getItem('rv_access_token');
        const res = await fetch(`${API_BASE}/api/manuscript-templates`, {
          credentials: 'omit',
          headers: {
            Accept: 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        });
        if (!res.ok) throw new Error(`Failed to load templates (${res.status})`);
        const data = await res.json();
        if (!cancelled) setTemplates(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const availableCategories = useMemo(
    () => Array.from(new Set(templates.map((t) => t.category))),
    [templates]
  );

  const visible = useMemo(
    () => (activeCategory === 'all' ? templates : templates.filter((t) => t.category === activeCategory)),
    [templates, activeCategory]
  );

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Not named `useTemplate`: anything starting with `use` is read as a hook by
  // react-hooks/rules-of-hooks, and passing it to a card's onUse is then a
  // build error.
  const chooseTemplate = (id) => {
    setChosenTemplateId(id);
    setCreateOpen(true);
  };

  return (
    // `100%`, not `100vh`: <main> in App.js already flex-grows to fill what the
    // Navbar and Footer leave, so a viewport-height page is taller than the
    // window by exactly those two — which puts the masthead's first rows under
    // the sticky Navbar as soon as anyone scrolls. Same as ManuscriptStructure.
    <Box sx={{ background: SP.lavender, minHeight: '100%' }}>
      {/* Masthead. The same purple ground the Manuscript Structure page uses, so
          the two read as parts of one product. */}
      <Box sx={{ background: SP.purpleDeep, color: SP.white, px: '28px', pt: '24px', pb: '38px' }}>
        <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/library')}
            sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, '&:hover': { color: SP.goldLight } }}
          >
            Back to Library
          </Button>

          <Box sx={{ textAlign: 'center', pt: '26px' }}>
            <Box sx={{ ...eyebrowSx, justifyContent: 'center', display: 'flex', color: SP.goldLight }}>
              Start a new project
            </Box>
            <Typography
              sx={{
                fontFamily: FONT.serif,
                fontWeight: 600,
                color: SP.white,
                fontSize: 'clamp(28px, 4vw, 40px)',
                mt: '12px',
              }}
            >
              Every project starts here
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: 14.5,
                mt: '12px',
                mx: 'auto',
                maxWidth: '74ch',
              }}
            >
              Pick a template built for the kind of writing you&rsquo;re doing, or start with a blank
              page. Templates set up the structure, so you can stay focused on the writing itself.
            </Typography>
          </Box>
        </Box>
      </Box>

      {!loading && !error && templates.length > 0 && (
        <CategoryFilter
          available={availableCategories}
          active={activeCategory}
          onChange={setActiveCategory}
        />
      )}

      <Box sx={{ maxWidth: 1320, mx: 'auto', px: '28px', pt: '40px', pb: '90px' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: '80px' }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Typography sx={{ textAlign: 'center', color: SP.gray, py: '80px' }} data-testid="template-error">
            {error}
          </Typography>
        )}

        {/* The catalogue is seeded data, so "none yet" is a real state on a
            fresh environment rather than an error. Say which script fills it. */}
        {!loading && !error && templates.length === 0 && (
          <Typography sx={{ textAlign: 'center', color: SP.gray, py: '80px' }} data-testid="template-empty">
            No templates have been published yet.
          </Typography>
        )}

        {!loading && !error && templates.length > 0 && (
          <Box
            data-testid="template-grid"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '20px',
              py: '40px',
              [BP.panelFloats]: { gridTemplateColumns: 'repeat(2, 1fr)' },
              [BP.phone]: { gridTemplateColumns: '1fr' },
            }}
          >
            {visible.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                expanded={expandedIds.has(template.id)}
                onToggleExpand={() => toggleExpanded(template.id)}
                onUse={() => chooseTemplate(template.id)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* The app's own Create dialog, not a copy of it — it arrives with the
          chosen template already selected and the author free to change it. */}
      <NewDocumentModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        initialTemplateId={chosenTemplateId}
      />
    </Box>
  );
}
